/**
 * RequestDetails — Payment forms (prepay, postpay) and invoice generation.
 * Mixed into RequestDetails.prototype.
 */
import { apiClient } from '../../utils/ApiClient.js';
import { Modal } from '../../utils/Modal.js';

export const PaymentsMixin = {

    formatInvoiceMoney(amount) {
        const n    = Number.parseFloat(amount);
        const safe = Number.isFinite(n) ? n : 0;
        return `$${safe.toFixed(2)}`;
    },

    // ── Inline Invoice Form ──────────────────────────────────────────────────

    requestHasRecordsToDownload() {
        return !!this.details?.querySelector('[data-file-category="records-to-download"]');
    },

    getRequestSubStatus() {
        const select = this.details?.querySelector('#sub_status');
        if (select) return String(select.value || '').trim();
        return '';
    },

    openMissingRecordsDownloadModal() {
        Modal.open('missing_records_download_modal');
    },

    closeMissingRecordsDownloadModal() {
        Modal.close('missing_records_download_modal');
    },

    async openInvoiceForm(form = null, triggerBtn = null) {
        if (!this.activeRequestId || !this.ajaxUrl) return;

        if (this.getRequestSubStatus() === 'completed' && !this.requestHasRecordsToDownload()) {
            this.openMissingRecordsDownloadModal();
            return;
        }

        const container = this.details?.querySelector('#invoice-form-inline');
        if (!container) return;

        this._invoiceForm       = form;
        this._invoiceTriggerBtn = triggerBtn;

        const services = JSON.parse(container.dataset.services || '[]');
        const defaults = JSON.parse(container.dataset.defaults || '[]');

        this.renderInvoiceFormRows(container, services, defaults);

        const noteWrap = container.querySelector('#invoice-decline-note-wrap');
        const noteArea = container.querySelector('#invoice-decline-note');
        if (noteWrap) {
            const isDecline = this._declineCancelIndex !== null && this._declineCancelIndex !== undefined;
            noteWrap.style.display = isDecline ? 'block' : 'none';
            if (noteArea) noteArea.value = '';
        }

        container.style.display = 'block';
        container.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

        const nonce = container.dataset.nonce
            || form?.querySelector('[name="request_workflow_nonce"]')?.value
            || '';

        try {
            const formData = new URLSearchParams();
            formData.append('action', 'docuswift_get_request_invoice_preview');
            formData.append('request_id', String(this.activeRequestId));
            formData.append('request_workflow_nonce', nonce);
            const res  = await fetch(this.ajaxUrl, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: formData.toString() });
            const json = await res.json();
            if (json.success && json.data) {
                const firstPrice = container.querySelector('#invoice-form-rows tr:first-child .invoice-row-price');
                if (firstPrice) {
                    firstPrice.value = Number(json.data.payments_total || 0).toFixed(2);
                    this.updateInvoiceFormTotal(container);
                }
            }
        } catch (_) { /* non-critical */ }
    },

    closeInvoiceForm() {
        const container = this.details?.querySelector('#invoice-form-inline');
        if (container) container.style.display = 'none';
        this._invoiceForm        = null;
        this._invoiceTriggerBtn  = null;
        this._declineCancelIndex = null;
    },

    renderInvoiceFormRows(container, services, rows) {
        const tbody = container.querySelector('#invoice-form-rows');
        if (!tbody) return;
        tbody.innerHTML = '';
        rows.forEach((row) => this.addInvoiceFormRow(tbody, services, row));
        this.updateInvoiceFormTotal(container);
    },

    addInvoiceFormRow(tbody, services, data = {}) {
        const esc       = (v) => String(v).replace(/"/g, '&quot;');
        const normalize = (v) => String(v || '').trim().toLowerCase().replace(/\s+/g, ' ');
        const tr        = document.createElement('tr');
        const presetsHtml = services.map((s) =>
            `<option value="${esc(s.name)}" data-description="${esc(s.description)}" data-price="${esc(s.price)}" data-item-id="${esc(s.item_id || '')}">${esc(s.name)}</option>`
        ).join('');
        const selected = data.service || '';
        tr.innerHTML = `
            <td><select class="invoice-row-service ds-nice-select">
                <option value=""${selected === '' ? ' selected' : ''}>— Custom —</option>
                <option value="Pre-payment Cost"${selected === 'Pre-payment Cost' ? ' selected' : ''}>Pre-payment Cost</option>
                ${presetsHtml}
            </select></td>
            <td><input type="text" class="invoice-row-description" value="${esc(data.description || '')}"></td>
            <td><input type="number" class="invoice-row-price" value="${data.price !== '' && data.price !== undefined ? Number(data.price).toFixed(2) : ''}" min="0" step="0.01" placeholder="0.00"></td>
            <input type="hidden" class="invoice-row-item-id" value="${esc(data.item_id || '')}">
            <td><button type="button" class="invoice-row-remove" aria-label="Remove row">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-trash"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M4 7l16 0"/><path d="M10 11l0 6"/><path d="M14 11l0 6"/><path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12"/><path d="M9 7v-3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3"/></svg>
            </button></td>`;

        const select = tr.querySelector('.invoice-row-service');
        const selectedNorm = normalize(selected);
        if (selectedNorm !== '') {
            let matchedOption = null;
            for (const opt of select.options) {
                if (normalize(opt.value) === selectedNorm) {
                    matchedOption = opt;
                    break;
                }
            }
            if (matchedOption) {
                select.value = matchedOption.value;
                for (const opt of select.options) {
                    if (opt === matchedOption) {
                        opt.selected = true;
                        opt.setAttribute('selected', 'selected');
                    } else {
                        opt.selected = false;
                        opt.removeAttribute('selected');
                    }
                }

                const descInput = tr.querySelector('.invoice-row-description');
                const itemIdInput = tr.querySelector('.invoice-row-item-id');
                if (descInput && (descInput.value || '').trim() === '' && matchedOption.dataset.description) {
                    descInput.value = matchedOption.dataset.description;
                }
                if (itemIdInput && (itemIdInput.value || '').trim() === '' && matchedOption.dataset.itemId) {
                    itemIdInput.value = matchedOption.dataset.itemId;
                }
            }
        }
        select.addEventListener('change', () => {
            const opt = select.selectedOptions[0];
            if (opt && opt.value !== '') {
                tr.querySelector('.invoice-row-description').value = opt.dataset.description || '';
                tr.querySelector('.invoice-row-price').value       = opt.dataset.price ? Number(opt.dataset.price).toFixed(2) : '';
                tr.querySelector('.invoice-row-item-id').value     = opt.dataset.itemId || '';
            }
            this.updateInvoiceFormTotal(tbody.closest('#invoice-form-inline'));
        });
        tr.querySelector('.invoice-row-price').addEventListener('input', () => {
            this.updateInvoiceFormTotal(tbody.closest('#invoice-form-inline'));
        });
        tbody.appendChild(tr);
    },

    updateInvoiceFormTotal(container) {
        if (!container) return;
        let total = 0;
        container.querySelectorAll('#invoice-form-rows .invoice-row-price').forEach((el) => {
            total += Math.max(0, Number.parseFloat(el.value) || 0);
        });
        const fee   = total * 0.03;
        const grand = total + fee;
        const totalEl = container.querySelector('#invoice-form-total');
        const feeEl   = container.querySelector('#invoice-form-qb-fee');
        const grandEl = container.querySelector('#invoice-form-grand-total');
        if (totalEl) totalEl.textContent = this.formatInvoiceMoney(total);
        if (feeEl)   feeEl.textContent   = this.formatInvoiceMoney(fee);
        if (grandEl) grandEl.textContent = this.formatInvoiceMoney(grand);
    },

    collectInvoiceFormLineItems(container) {
        const items = [];
        container.querySelectorAll('#invoice-form-rows tr').forEach((tr) => {
            const price = Math.max(0, Number.parseFloat(tr.querySelector('.invoice-row-price')?.value) || 0);
            if (price <= 0) return;
            items.push({
                service:     tr.querySelector('.invoice-row-service')?.value     || '',
                description: tr.querySelector('.invoice-row-description')?.value || '',
                price,
                item_id:     tr.querySelector('.invoice-row-item-id')?.value     || '',
            });
        });
        return items;
    },

    async submitInvoiceForm(confirmBtn) {
        const form = this._invoiceForm;
        if (!this.activeRequestId || !this.ajaxUrl) return;

        const container = this.details?.querySelector('#invoice-form-inline');
        if (!container) return;

        const items   = this.collectInvoiceFormLineItems(container);
        const errorEl = container.querySelector('#invoice-form-error');

        if (items.length === 0) {
            if (errorEl) { errorEl.textContent = 'Add at least one line item with a price.'; errorEl.style.display = 'block'; }
            return;
        }

        const subTotal            = items.reduce((sum, it) => sum + it.price, 0);
        const fee                 = Math.round(subTotal * 0.03 * 100) / 100;
        const processingFeeItemId = container.dataset.processingFeeItemId || '';
        if (fee > 0) {
            items.push({ service: 'QuickBooks Processing Fee', description: 'QuickBooks Processing Fee (3%)', price: fee, item_id: processingFeeItemId });
        }

        const declineIndex = this._declineCancelIndex ?? null;
        if (declineIndex !== null) {
            const noteArea = container.querySelector('#invoice-decline-note');
            const note = noteArea?.value?.trim() || '';
            if (!note) {
                noteArea?.classList.add('input-error');
                noteArea?.focus();
                noteArea?.addEventListener('input', () => noteArea.classList.remove('input-error'), { once: true });
                if (errorEl) { errorEl.textContent = 'Please add a decline note.'; errorEl.style.display = 'block'; }
                return;
            }
        }

        if (errorEl) { errorEl.style.display = 'none'; errorEl.textContent = ''; }
        this.setBtnLoading(confirmBtn, true);

        const nonce = container.dataset.nonce
            || form?.querySelector('[name="request_workflow_nonce"]')?.value
            || '';

        const formData = new URLSearchParams();
        formData.append('action', 'docuswift_generate_request_invoice');
        formData.append('request_id', String(this.activeRequestId));
        formData.append('workflow_step_key', form?.querySelector('[name="workflow_step_key"]')?.value || form?.getAttribute('data-workflow-step') || '');
        formData.append('request_workflow_nonce', nonce);
        formData.append('line_items', JSON.stringify(items));
        if (declineIndex !== null) {
            const noteArea = container.querySelector('#invoice-decline-note');
            formData.append('decline_cancel_index', String(declineIndex));
            formData.append('decline_note', noteArea?.value?.trim() || '');
        }

        try {
            const res  = await fetch(this.ajaxUrl, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: formData.toString() });
            const json = await res.json();
            this.setBtnLoading(confirmBtn, false);

            if (!json.success) {
                const msg = json.data?.message || 'Could not generate invoice.';
                if (errorEl) { errorEl.textContent = msg; errorEl.style.display = 'block'; }
                else window.alert(msg);
                return;
            }

            if (declineIndex !== null) {
                const block = document.getElementById('cancel-request-pending-block');
                if (block) block.remove();
            }
            this.closeInvoiceForm();
            this.refreshActivityLists(json.data || {});
            this.showToast(json.data?.message || 'Invoice generated.');
            this.fetchDetails(String(this.activeRequestId));
            const paymentsBox = this.details?.querySelector('#payments_box');
            if (paymentsBox) paymentsBox.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch (err) {
            console.error(err);
            this.setBtnLoading(confirmBtn, false);
            this.showToast('Could not generate invoice.', 'error');
        }
    },

    // ── Payment Edit Modal ───────────────────────────────────────────────────

    openPaymentEditModal(btn, type) {
        const modal = document.getElementById('payment_edit_modal');
        if (!modal) return;

        this._paymentEditType        = type;
        this._paymentEditRequestId   = btn.dataset.requestId || String(this.activeRequestId || '');
        this._paymentEditNonceAdd    = btn.dataset.nonceAdd    || '';
        this._paymentEditNonceManage = btn.dataset.nonceManage || '';

        const payments = JSON.parse(btn.dataset.payments || '[]');

        const titleEl = document.getElementById('payment_edit_modal_title');
        if (titleEl) titleEl.textContent = type === 'prepay' ? 'Edit PrePay' : 'Edit PostPay';

        this._renderPaymentEditList(payments, type);
        this._resetPaymentEditForm(type);

        document.getElementById('payment_edit_type').value         = type;
        document.getElementById('payment_edit_nonce_add').value    = this._paymentEditNonceAdd;
        document.getElementById('payment_edit_nonce_manage').value = this._paymentEditNonceManage;

        Modal.open('payment_edit_modal');

        // Init flatpickr on the date field (modal is in DOM but was hidden before).
        const dateEl = document.getElementById('payment_edit_date');
        if (dateEl && !dateEl._flatpickr) {
            import('../../components/DatePicker.js').then(({ DatePicker }) => {
                DatePicker.initInput(dateEl);
            });
        }

        // NOTE: the FileUpload component is already initialised globally by
        // app.js via FileUpload.initAll('.file-upload-container'). Do NOT
        // initialise it again here — a second instance adds a second click
        // handler and the file picker opens twice.
    },

    closePaymentEditModal() {
        Modal.close('payment_edit_modal');
    },

    _renderPaymentEditList(payments, type) {
        const list = document.getElementById('payment_edit_list');
        if (!list) return;

        if (!payments || payments.length === 0) {
            list.innerHTML = '<p class="payment-edit-list__empty">No payments added yet.</p>';
            return;
        }

        const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
        const prefix = type;

        list.innerHTML = payments.map((p, i) => {
            const amount  = parseFloat(p[`${prefix}_amount`]       || 0).toFixed(2);
            const rawDate = p[`${prefix}_date`] || '';
            const date    = rawDate ? new Date(rawDate + 'T12:00:00').toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '—';
            const label   = esc(p[`${prefix}_payment_type`] || '');
            return `
                <div class="payment-edit-list__item" data-index="${i}">
                    <div class="payment-edit-list__info">
                        <span class="payment-edit-list__amount">$${amount}</span>
                        <span class="payment-edit-list__meta">${label} · ${date}</span>
                    </div>
                    <div class="payment-edit-list__actions">
                        <button type="button" class="payment-edit-list__edit-btn icon-btn"
                            data-index="${i}"
                            data-amount="${esc(p[`${prefix}_amount`] || '')}"
                            data-date="${esc(rawDate)}"
                            data-label="${esc(p[`${prefix}_payment_type`] || '')}"
                            title="Edit">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1"/><path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415"/><path d="M16 5l3 3"/></svg>
                        </button>
                        <button type="button" class="payment-edit-list__delete-btn icon-btn icon-btn--danger"
                            data-index="${i}"
                            title="Delete">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M4 7l16 0"/><path d="M10 11l0 6"/><path d="M14 11l0 6"/><path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12"/><path d="M9 7v-3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3"/></svg>
                        </button>
                    </div>
                </div>`;
        }).join('');
    },

    _resetPaymentEditForm(type) {
        const amountEl  = document.getElementById('payment_edit_amount');
        const dateEl    = document.getElementById('payment_edit_date');
        const typeEl    = document.getElementById('payment_edit_payment_type');
        const indexEl   = document.getElementById('payment_edit_index');
        const saveBtn   = document.getElementById('payment_edit_form_save');
        const titleEl   = document.getElementById('payment_edit_form_title');
        const errorEl   = document.getElementById('payment_edit_form_error');
        const fileEl    = document.getElementById('payment_edit_invoice_file');
        const fileMarker = document.getElementById('payment_edit_file_required_marker');
        const fileHint  = document.getElementById('payment_edit_file_hint');

        if (amountEl)  amountEl.value  = '';
        if (dateEl) {
            if (dateEl._flatpickr) dateEl._flatpickr.clear();
            else dateEl.value = '';
        }
        if (typeEl)    typeEl.value    = '';
        if (indexEl)   indexEl.value   = '-1';
        if (saveBtn)   saveBtn.textContent  = 'Add Payment';
        if (titleEl)   titleEl.textContent  = `Add ${type === 'prepay' ? 'PrePay' : 'PostPay'}`;
        if (errorEl) { errorEl.style.display = 'none'; errorEl.textContent = ''; }

        // Reset file upload — clear the input and let the globally-registered
        // FileUpload instance restore its preview via the change event.
        if (fileEl) {
            fileEl.value = '';
            fileEl.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // File is required when adding new.
        if (fileMarker) fileMarker.style.display = '';
        if (fileHint)   fileHint.textContent = 'Attach the payment invoice document. Required for new entries.';

        amountEl?.classList.remove('input-error');
        dateEl?.classList.remove('input-error');
        typeEl?.classList.remove('input-error');
        fileEl?.classList.remove('input-error');
    },

    _setPaymentEditFormEditMode(btn) {
        const index  = btn.dataset.index;
        const amount = btn.dataset.amount || '';
        const date   = btn.dataset.date   || '';
        const label  = btn.dataset.label  || '';
        const type   = this._paymentEditType;

        document.getElementById('payment_edit_amount').value = amount;
        const dateEl = document.getElementById('payment_edit_date');
        if (dateEl) {
            if (dateEl._flatpickr) dateEl._flatpickr.setDate(date, true);
            else dateEl.value = date;
        }
        document.getElementById('payment_edit_payment_type').value = label;
        document.getElementById('payment_edit_index').value        = index;

        // File is optional when editing an existing entry.
        const fileMarker = document.getElementById('payment_edit_file_required_marker');
        const fileHint   = document.getElementById('payment_edit_file_hint');
        const fileEl     = document.getElementById('payment_edit_invoice_file');
        if (fileMarker) fileMarker.style.display = 'none';
        if (fileHint)   fileHint.textContent = 'Optional: attach a new invoice file to replace the existing one.';
        if (fileEl) {
            fileEl.value = '';
            fileEl.dispatchEvent(new Event('change', { bubbles: true }));
        }
        fileEl?.classList.remove('input-error');

        const saveBtn = document.getElementById('payment_edit_form_save');
        if (saveBtn) saveBtn.textContent = 'Save Changes';

        const titleEl = document.getElementById('payment_edit_form_title');
        if (titleEl) titleEl.textContent = `Edit ${type === 'prepay' ? 'PrePay' : 'PostPay'}`;

        document.getElementById('payment_edit_amount')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        document.getElementById('payment_edit_amount')?.focus();
    },

    async handlePaymentEditListClick(e) {
        const delBtn  = e.target.closest('.payment-edit-list__delete-btn');
        const editBtn = e.target.closest('.payment-edit-list__edit-btn');

        if (delBtn) {
            e.preventDefault();
            await this._deletePaymentEntry(delBtn);
        } else if (editBtn) {
            e.preventDefault();
            this._setPaymentEditFormEditMode(editBtn);
        }
    },

    async _deletePaymentEntry(btn) {
        const index       = parseInt(btn.dataset.index ?? '-1', 10);
        const requestId   = this._paymentEditRequestId;
        const type        = this._paymentEditType;
        const nonceManage = this._paymentEditNonceManage;

        if (index < 0 || !requestId || !type) return;

        this.setBtnLoading(btn, true);

        try {
            const body = new URLSearchParams({
                action:        'docuswift_delete_payment_entry',
                nonce:         nonceManage,
                request_id:    requestId,
                payment_type:  type,
                payment_index: String(index),
            });

            const res  = await fetch(this.ajaxUrl, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() });
            const json = await res.json();

            if (!json.success) {
                this.setBtnLoading(btn, false);
                this.showToast(json.data?.message || 'Could not delete payment.', 'error');
                return;
            }

            // Remove row from modal list
            btn.closest('.payment-edit-list__item')?.remove();
            const list = document.getElementById('payment_edit_list');
            if (list && !list.querySelector('.payment-edit-list__item')) {
                list.innerHTML = '<p class="payment-edit-list__empty">No payments added yet.</p>';
            }

            // Re-index remaining delete/edit buttons
            list?.querySelectorAll('.payment-edit-list__item').forEach((item, newIdx) => {
                item.dataset.index = newIdx;
                item.querySelector('.payment-edit-list__delete-btn')?.setAttribute('data-index', newIdx);
                item.querySelector('.payment-edit-list__edit-btn')?.setAttribute('data-index', newIdx);
            });

            // Refresh underlying panel silently
            if (this.activeRequestId) this.fetchDetails(String(this.activeRequestId));

            this.showToast('Payment deleted.');
        } catch (err) {
            console.error(err);
            this.setBtnLoading(btn, false);
            this.showToast('Could not delete payment.', 'error');
        }
    },

    async handlePaymentEditFormSubmit(e) {
        e.preventDefault();

        const type        = this._paymentEditType;
        const requestId   = this._paymentEditRequestId;
        const index       = parseInt(document.getElementById('payment_edit_index')?.value ?? '-1', 10);
        const nonceAdd    = document.getElementById('payment_edit_nonce_add')?.value    || '';
        const nonceManage = document.getElementById('payment_edit_nonce_manage')?.value || '';
        const amount      = (document.getElementById('payment_edit_amount')?.value || '').trim();
        const dateRaw     = (document.getElementById('payment_edit_date')?.value   || '').trim();
        // Convert MM/DD/YYYY (flatpickr display) → YYYY-MM-DD for PHP storage.
        const mdyMatch    = dateRaw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        const date        = mdyMatch ? `${mdyMatch[3]}-${mdyMatch[1].padStart(2,'0')}-${mdyMatch[2].padStart(2,'0')}` : dateRaw;
        const label       = (document.getElementById('payment_edit_payment_type')?.value || '').trim();
        const fileEl      = document.getElementById('payment_edit_invoice_file');
        const file        = fileEl?.files?.[0] || null;
        const saveBtn     = document.getElementById('payment_edit_form_save');
        const errorEl     = document.getElementById('payment_edit_form_error');

        let hasError = false;
        [['payment_edit_amount', amount], ['payment_edit_date', date], ['payment_edit_payment_type', label]].forEach(([id, val]) => {
            const el = document.getElementById(id);
            if (!val) { el?.classList.add('input-error'); hasError = true; }
            else el?.classList.remove('input-error');
        });

        // Invoice file is required for new entries (index === -1).
        if (index < 0 && !file) {
            fileEl?.classList.add('input-error');
            hasError = true;
        } else {
            fileEl?.classList.remove('input-error');
        }

        if (hasError) {
            if (errorEl) { errorEl.textContent = 'All fields are required.'; errorEl.style.display = 'block'; }
            return;
        }
        if (errorEl) { errorEl.style.display = 'none'; errorEl.textContent = ''; }

        this.setBtnLoading(saveBtn, true);

        try {
            // Use FormData so the file is transmitted as multipart/form-data.
            const body = new FormData();

            if (index >= 0) {
                body.append('action',        'docuswift_update_payment_entry');
                body.append('nonce',         nonceManage);
                body.append('request_id',    requestId);
                body.append('payment_type',  type);
                body.append('payment_index', String(index));
                body.append('amount',        amount);
                body.append('date',          date);
                body.append('payment_label', label);
            } else {
                const nonceField = type === 'prepay' ? 'prepay_edit_nonce' : 'postpay_edit_nonce';
                const action     = type === 'prepay' ? 'submit_prepay_edits' : 'submit_postpay_edits';
                body.append('action',      action);
                body.append(nonceField,    nonceAdd);
                body.append('request_id',  requestId);
                body.append('amount',      amount);
                body.append('date',        date);
                body.append('payment_type', label);
            }

            if (file) {
                body.append('invoice_file', file, file.name);
            }

            // Do NOT set Content-Type — browser sets it with correct boundary for multipart.
            const res  = await fetch(this.ajaxUrl, { method: 'POST', body });
            const json = await res.json();

            this.setBtnLoading(saveBtn, false);

            if (!json.success) {
                const msg = json.data?.message || 'Something went wrong.';
                if (errorEl) { errorEl.textContent = msg; errorEl.style.display = 'block'; }
                else this.showToast(msg, 'error');
                return;
            }

            this.closePaymentEditModal();
            this.showToast(json.data?.message || 'Payment saved.');
            if (this.activeRequestId) this.fetchDetails(String(this.activeRequestId));

        } catch (err) {
            console.error(err);
            this.setBtnLoading(saveBtn, false);
            this.showToast('Could not save payment.', 'error');
        }
    },

    // ── Prepay / Postpay forms ───────────────────────────────────────────────

    async handlePrepaySubmit(e) {
        e.preventDefault();
        const form    = e.target;
        const saveBtn = form.querySelector('button[type="submit"]');
        this.setBtnLoading(saveBtn, true);
        try {
            const formData = new FormData(form);
            formData.append('action', this.apiAction);
            formData.append('request_id', this.activeRequestId);
            const response = await fetch(this.ajaxUrl, { method: 'POST', body: formData });
            const result   = await response.json();
            if (result.success) {
                form.reset();
                form.classList.remove('active');
                this.fetchDetails(String(this.activeRequestId));
            } else {
                this.setBtnLoading(saveBtn, false);
                window.alert(result.data?.message || 'Something went wrong');
            }
        } catch (error) {
            console.error('AJAX Error:', error);
            this.setBtnLoading(saveBtn, false);
        }
    },

    async handlePostPaySubmit(e) {
        e.preventDefault();
        const form    = e.target;
        const saveBtn = form.querySelector('button[type="submit"]');
        this.setBtnLoading(saveBtn, true);
        try {
            const formData = new FormData(form);
            formData.append('action', this.apiActionPostPay);
            formData.append('request_id', this.activeRequestId);
            const response = await fetch(this.ajaxUrl, { method: 'POST', body: formData });
            const result   = await response.json();
            if (result.success) {
                form.reset();
                form.classList.remove('active');
                this.fetchDetails(String(this.activeRequestId));
            } else {
                this.setBtnLoading(saveBtn, false);
                window.alert(result.data?.message || 'Something went wrong');
            }
        } catch (error) {
            console.error('AJAX Error:', error);
            this.setBtnLoading(saveBtn, false);
        }
    },
};
